import React from 'react';
import { StyleSheet } from 'react-native';

import { DoubleArrow } from '@/assets/icons/DoubleArrow';
import { Text } from '@/components/TextComponent';
import type { TextProps } from '@/components/TextComponent';
import { Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { common } from '@/styles/styles';

import { EntryStatusBadge } from './EntryStatusBadge';
import type { EntryStatus } from './EntryStatusBadge';
import { SafetyShadow } from './SafetyShadow';
import { useIsTextClipped } from './use-is-text-clipped';

export type EntryCardHeaderProps = {
    title: string;
    subtitle: string;
    trailing?: string;
    trailingColor?: TextProps['color'];
    initialMultiplier?: number;
    hideMultiplier?: boolean;
    status?: EntryStatus;
    titleColor?: TextProps['color'];
};

export const EntryCardHeader = ({
    title,
    subtitle,
    trailing,
    trailingColor = ds.colorToken.text.primary,
    status,
    titleColor = ds.colorToken.text.primary,
    initialMultiplier,
    hideMultiplier,
}: EntryCardHeaderProps) => {
    const { isClipped, onTextLayout } = useIsTextClipped();

    return (
        <Row paddingHorizontal="s16" justifyContent="space-between" alignItems="flex-start" gap="s16">
            <Column flex={1} gap="s2">
                <Row alignItems="center" gap="s4">
                    {/* Title shrinks and clips when it hits the pinned arrow/trailing group. */}
                    <Row flexShrink={1} overflow="hidden">
                        <Text
                            variant="titleMedium2"
                            lineHeight={20}
                            color={titleColor}
                            numberOfLines={1}
                            ellipsizeMode="clip"
                            flexShrink={1}
                        >
                            {title}
                        </Text>
                        {/* Invisible, width-constrained copy: wraps to >1 line exactly when the visible
                            single-line title clips, which drives the safety shadow below. */}
                        <Text
                            variant="titleMedium2"
                            lineHeight={20}
                            onTextLayout={onTextLayout}
                            style={common.measureLayer}
                            accessibilityElementsHidden
                            importantForAccessibility="no-hide-descendants"
                        >
                            {title}
                        </Text>
                    </Row>
                    {/* Pinned (never shrinks): the safety shadow dissolves the clipped title into the card,
                        then the double arrow Figma always renders, then the trailing value. */}
                    <Row
                        flexShrink={0}
                        alignItems="center"
                        gap="s4"
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                    >
                        {isClipped ? <SafetyShadow style={styles.shadow} /> : null}
                        {hideMultiplier ? null : (
                            <>
                                <DoubleArrow />
                                {trailing ? (
                                    <Text variant="titleMedium2" lineHeight={20} color={trailingColor}>
                                        {trailing}
                                    </Text>
                                ) : null}
                                {initialMultiplier ? (
                                    <Text
                                        variant="titleMedium2"
                                        lineHeight={20}
                                        color={trailingColor}
                                        textDecorationLine={'line-through'}
                                        opacity={0.5}
                                    >
                                        {initialMultiplier}x
                                    </Text>
                                ) : null}
                            </>
                        )}
                    </Row>
                </Row>
                <Text variant="bodySmall2" color="neutral300" numberOfLines={1}>
                    {subtitle}
                </Text>
            </Column>
            {status ? <EntryStatusBadge status={status} /> : null}
        </Row>
    );
};

const styles = StyleSheet.create({
    // 24px fade sitting just left of the pinned arrow, dissolving the clipped title into the card.
    shadow: { position: 'absolute', left: -24, top: 0, bottom: 0, width: 24 },
});
